import { PermissionKey, getPermissionDefinition, hasPermissionV2 } from '@c4ytechnology/authz';
import type { AdminConsoleAccess } from '../api/adminConsole';

// RBAC Phase 2 delegation rule, mirrored from admin-api `iamGuardrails` so the SPA can grey out
// what the server would refuse. The server stays authoritative; its error is always shown.
//
//   isDelegableRole = roleKey !== 'super_admin' && no permission is an IAM key ('iam.' prefix or
//                     catalog group 'IAM')
//   grant / edit / revoke: never on yourself (SELF_GRANT); needs iam.assignments.write;
//   a non-delegable role also needs the actor to hold every key in it (ESCALATION).
//   A role whose permissions are unknown (no iam.roles.read, or still loading) is treated as
//   non-delegable: only a Super Admin viewer may act on it (ROLE_UNVERIFIED).

export const SUPER_ADMIN_ROLE_KEY = 'super_admin';

export type IamActor = { id: string; access?: AdminConsoleAccess | null } | null | undefined;

/** `permissions: null` = not known to the SPA (no iam.roles.read, or still loading). */
export type IamRoleLike = { key: string; permissions: readonly string[] | null };

export const ROLE_UNVERIFIED_REASON = "Can't verify this role's permissions";

export function isSuperAdminRoleKey(roleKey: string | null | undefined): boolean {
  return roleKey === SUPER_ADMIN_ROLE_KEY;
}

/** Mirrors admin-api: an IAM key is one prefixed 'iam.' or in the catalog's 'IAM' group. */
export function isIamPermissionKey(key: string): boolean {
  return key.startsWith('iam.') || getPermissionDefinition(key)?.group === 'IAM';
}

export function isDelegableRole(roleKey: string, rolePermissions: readonly string[] | null): boolean {
  if (rolePermissions === null) return false;
  return !isSuperAdminRoleKey(roleKey) && !rolePermissions.some(isIamPermissionKey);
}

/** The viewer holds the Super Admin role (and with it every key). */
export function actorIsSuperAdmin(actor: IamActor): boolean {
  const access = actor?.access;
  if (!access) return false;
  return isSuperAdminRoleKey(access.role) || (access.roles ?? []).some((r) => isSuperAdminRoleKey(r.key));
}

/** Role keys the actor does not hold (what an escalation check would reject). */
export function keysActorLacks(actor: IamActor, keys: readonly string[]): string[] {
  const held = new Set(actor?.access?.permissions ?? []);
  return keys.filter((k) => !held.has(k));
}

export type Grantability = { allowed: true } | { allowed: false; reason: string; code: string };

/**
 * Whether `actor` may grant (or edit/revoke an assignment of) `role` for `targetUserId`.
 * `reason` is shown as a tooltip/helper next to a greyed-out option.
 */
export function roleGrantability(actor: IamActor, role: IamRoleLike, targetUserId: string): Grantability {
  if (!actor) return { allowed: false, code: 'UNAUTHENTICATED', reason: 'Sign in to manage roles.' };
  if (actor.id === targetUserId) {
    return { allowed: false, code: 'SELF_GRANT', reason: 'You cannot change your own roles.' };
  }
  if (!hasPermissionV2(actor.access, PermissionKey.IAM_ASSIGNMENTS_WRITE)) {
    return {
      allowed: false,
      code: 'FORBIDDEN',
      reason: 'Needs the "Assign roles to staff" permission (iam.assignments.write).',
    };
  }
  if (role.permissions === null) {
    if (actorIsSuperAdmin(actor)) return { allowed: true };
    return { allowed: false, code: 'ROLE_UNVERIFIED', reason: ROLE_UNVERIFIED_REASON };
  }
  if (isDelegableRole(role.key, role.permissions)) return { allowed: true };
  const missing = keysActorLacks(actor, role.permissions);
  if (missing.length > 0) {
    return {
      allowed: false,
      code: 'ESCALATION',
      reason: `This role holds ${missing.length} permission${missing.length === 1 ? '' : 's'} you do not hold.`,
    };
  }
  return { allowed: true };
}

/** Super Admin assignments never expire: the expiry field is hidden and forced empty. */
export function roleAllowsExpiry(roleKey: string | null | undefined): boolean {
  return !isSuperAdminRoleKey(roleKey);
}

/** Plain-language hint for admin-api IAM error codes (shown under the server's own message). */
export const IAM_ERROR_HINTS: Readonly<Record<string, string>> = {
  SELF_GRANT: 'You cannot grant, change or revoke your own roles. Ask another administrator.',
  ESCALATION: 'This role holds permissions you do not hold, so you cannot grant, change or revoke it.',
  SUPER_ADMIN_ONLY: 'Only a Super Admin can do this.',
  LAST_SUPER_ADMIN: 'At least one active Super Admin must remain.',
  MFA_REQUIRED: 'This action needs a sign-in with your authenticator (MFA).',
  GROUPS_READ_ONLY: 'Groups are read-only now that roles decide access.',
  REVIEW_CLOSED: 'This access review is closed.',
  SYSTEM_ROLE: 'Super Admin cannot be edited, and system roles cannot be deleted.',
  ROLE_IN_USE: 'Remove every assignment of this role before deleting it.',
  MFA_ATTESTATION_INVALID: 'The MFA hand-off was rejected. Sign in again with your authenticator.',
  // RBAC Phase 4: a critical key needs a passkey session.
  PHR_REQUIRED: 'This action needs a passkey sign-in. Sign in again with your passkey (or register one first).',
  // RBAC Phase 4 just-in-time access.
  JIT_DISABLED: 'Just-in-time access is turned off in this environment.',
  JIT_ELIGIBILITY_REQUIRED:
    'This is a critical role: it cannot be granted directly (with or without an expiry). Make the user eligible; they then request (activate) it when needed.',
  BREAK_GLASS_PROTECTED: 'This is a break-glass account. Its Super Admin, active state and deletion are managed with the rbac:jit-backfill CLI only.',
  JIT_POLICY_TOO_WEAK: 'Critical roles need approval by a holder of the same role, an IAM Admin or a Super Admin.',
  NOT_ELIGIBLE: 'You are not eligible for this role. Ask an IAM Admin to add an eligibility.',
  DURATION_TOO_LONG: 'Choose a shorter duration; the eligibility sets the maximum.',
  ACTIVATION_OPEN: 'You already have a pending or active activation of this role.',
  SELF_APPROVAL: 'Someone else must approve or reject your own request.',
  NOT_APPROVER: 'Only holders (or eligibles) of the approver role can decide this request.',
  ACTIVATION_NOT_PENDING: 'This request was already decided, cancelled or has expired.',
  ACTIVATION_NOT_ACTIVE: 'This activation has already ended or expired.',
};

export type IamErrorView = { message: string; code?: string; hint?: string };

/** `{ error, code? }` from an admin-api axios error, with a hint for known codes. */
export function iamErrorView(err: unknown, fallbackMessage: string): IamErrorView {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  let message = '';
  let code: string | undefined;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (typeof o.error === 'string' && o.error.trim()) message = o.error.trim();
    else if (typeof o.message === 'string' && o.message.trim()) message = o.message.trim();
    if (typeof o.code === 'string' && o.code.trim()) code = o.code.trim();
  }
  if (!message) message = fallbackMessage;
  return { message, ...(code ? { code } : {}), ...(code && IAM_ERROR_HINTS[code] ? { hint: IAM_ERROR_HINTS[code] } : {}) };
}
